import React, { useCallback, useEffect, useState } from "react";

import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import RocketLaunchIcon from "@mui/icons-material/RocketLaunch";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Divider,
  MobileStepper,
  Paper,
  Stack,
  Step,
  StepLabel,
  Stepper,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import axios from "axios";
import { useSearchParams } from "react-router";

import { ApplicationEndpoints } from "@generated/application";
import { apiBaseUrls } from "src/platform/constants/shared-constants";

import {
  CloudProvider,
  InfraType,
  StackTemplate,
  StackType,
} from "../../types";
import { StepChooseCloud } from "../CreateStackPage/StepChooseCloud";
import { StepChooseInfraTarget } from "../CreateStackPage/StepChooseInfraTarget";
import StepChooseTemplate from "../CreateStackPage/StepChooseTemplate";
import { StepChooseType } from "../CreateStackPage/StepChooseType";
import { StepNameAndConfirm } from "../CreateStackPage/StepNameAndConfirm";

import type { WizardEditPageProps, WizardState } from "./types";
import {
  P,
  STEPS,
  authHeader,
  isStepValid,
  parseFromParams,
  serializeToParams,
} from "./wizard-helpers";

export default function WizardEditPage({
  system,
  orgAlias,
  sysAlias,
  onDone,
}: WizardEditPageProps) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [searchParams, setSearchParams] = useSearchParams();

  const [hydrated, setHydrated] = useState(false);
  const { step: initialStep, state: initialState } =
    parseFromParams(searchParams);
  const [activeStep, setActiveStep] = useState(initialStep);
  const [state, setState] = useState<WizardState>(initialState);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Hydrate wizard state from system on first load (no URL params set yet)
  useEffect(() => {
    if (hydrated || searchParams.get(P.TYPE)) {
      setHydrated(true);
      return;
    }
    const newState: WizardState = {
      stackType: (system.systemType as StackType | null) ?? null,
      cloudProvider: (system.cloudProvider as CloudProvider | null) ?? null,
      infraType: (system.infraType as InfraType | null) ?? null,
      infraTarget: null,
      targetStepDone:
        system.infraType === "rusha_hosted" || system.targetId != null,
      selectedTemplate: null,
      name: system.name,
      region: system.region ?? "",
    };
    // Start at step 3 (template) so StepChooseTemplate can auto-select the slug
    // before the user reaches the review step — avoids a blank review page.
    const params: Record<string, string> = {
      ...serializeToParams(3, newState),
      [P.STEP]: "3",
    };
    if (system.templateSlug) params[P.TEMPLATE] = system.templateSlug;
    if (system.targetId) params[P.TARGET_ID] = String(system.targetId);
    setSearchParams(params, { replace: true });
    setState(newState);
    setActiveStep(3);
    setHydrated(true);
  }, [system]);

  // Sync wizard state → URL
  useEffect(() => {
    if (!hydrated) return;
    setSearchParams(serializeToParams(activeStep, state), { replace: true });
  }, [activeStep, state, hydrated, setSearchParams]);

  const preselectedTargetId = searchParams.get(P.TARGET_ID)
    ? Number(searchParams.get(P.TARGET_ID))
    : undefined;
  const preselectedTemplateSlug = searchParams.get(P.TEMPLATE) ?? undefined;
  const canProceed = isStepValid(activeStep, state);

  const handleTemplateChange = useCallback(
    (t: StackTemplate | null) =>
      setState((s) => ({ ...s, selectedTemplate: t })),
    [],
  );

  async function handleSubmit() {
    if (
      !state.stackType ||
      !state.cloudProvider ||
      !state.infraType ||
      !state.selectedTemplate
    )
      return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      const url = `${apiBaseUrls.BACKEND}${ApplicationEndpoints.UPDATE_APPLICATION.replace(":id", sysAlias)}`;
      await axios.patch(
        url,
        {
          name: state.name.trim(),
          systemType: state.stackType,
          cloudProvider: state.cloudProvider,
          infraType: state.infraType,
          targetId: state.infraTarget?.id,
          templateSlug: state.selectedTemplate.slug,
          stackConfig: state.selectedTemplate.stackConfig,
          region: state.region,
          deploy: true,
        },
        { headers: authHeader() },
      );
      setSearchParams({});
      onDone();
    } catch (err) {
      const msg = axios.isAxiosError(err)
        ? ((err.response?.data as { message?: string })?.message ?? err.message)
        : err instanceof Error
          ? err.message
          : "Failed to save.";
      setSubmitError(msg);
    } finally {
      setSubmitting(false);
    }
  }

  function renderStep() {
    if (activeStep === 0) {
      return (
        <StepChooseType
          value={state.stackType}
          onChange={(t) =>
            setState((s) => ({
              ...s,
              stackType: t,
              selectedTemplate: null,
              cloudProvider: null,
              infraType: null,
              infraTarget: null,
              targetStepDone: false,
            }))
          }
        />
      );
    }
    if (activeStep === 1) {
      return (
        <StepChooseCloud
          cloudProvider={state.cloudProvider}
          infraType={state.infraType}
          onCloudProviderChange={(p) =>
            setState((s) => ({
              ...s,
              cloudProvider: p,
              infraType: null,
              infraTarget: null,
              targetStepDone: false,
              selectedTemplate: null,
            }))
          }
          onInfraTypeChange={(infra) =>
            setState((s) => ({
              ...s,
              infraType: infra,
              infraTarget: null,
              targetStepDone: false,
              selectedTemplate: null,
            }))
          }
        />
      );
    }
    if (activeStep === 2 && orgAlias && state.infraType) {
      return (
        <StepChooseInfraTarget
          organizationAlias={orgAlias}
          infraType={state.infraType}
          value={state.infraTarget}
          preselectedId={preselectedTargetId}
          onChange={(target) =>
            setState((s) => ({
              ...s,
              infraTarget: target,
              targetStepDone: true,
            }))
          }
        />
      );
    }
    if (activeStep === 3 && state.stackType && state.infraType) {
      return (
        <StepChooseTemplate
          stackType={state.stackType}
          infraType={state.infraType}
          value={state.selectedTemplate}
          preselectedSlug={preselectedTemplateSlug}
          onChange={handleTemplateChange}
        />
      );
    }
    if (
      activeStep === 4 &&
      state.stackType &&
      state.cloudProvider &&
      state.selectedTemplate
    ) {
      return (
        <Box>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
            <Typography variant="body2" color="text.secondary">
              Stack alias (fixed):
            </Typography>
            <Chip
              label={sysAlias}
              size="small"
              variant="outlined"
              sx={{ fontFamily: "monospace" }}
            />
          </Stack>
          <Divider sx={{ mb: 2 }} />
          <StepNameAndConfirm
            stackType={state.stackType}
            cloudProvider={state.cloudProvider}
            selectedTemplate={state.selectedTemplate}
            name={state.name}
            region={state.region}
            onNameChange={(n) => setState((s) => ({ ...s, name: n }))}
            onRegionChange={(r) => setState((s) => ({ ...s, region: r }))}
          />
        </Box>
      );
    }
    // Shouldn't reach here — show spinner while template auto-selects
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
        <CircularProgress size={32} />
      </Box>
    );
  }

  const backLabel = activeStep === 0 ? "Cancel" : "Back";
  const backAction =
    activeStep === 0 ? onDone : () => setActiveStep((s) => Math.max(0, s - 1));

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1 }}>
        <Typography variant="h5" fontWeight={700}>
          Reconfigure Stack
        </Typography>
        <Chip
          label={sysAlias}
          size="small"
          variant="outlined"
          sx={{ fontFamily: "monospace" }}
        />
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        All infrastructure fields are editable — this stack has not been
        deployed yet.
      </Typography>

      {!isMobile && (
        <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
          {STEPS.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
      )}

      <Paper variant="outlined" sx={{ p: { xs: 2, sm: 4 }, mb: 3 }}>
        {renderStep()}
      </Paper>

      {submitError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {submitError}
        </Alert>
      )}

      {isMobile ? (
        <MobileStepper
          variant="dots"
          steps={STEPS.length}
          position="static"
          activeStep={activeStep}
          backButton={
            <Button size="small" onClick={backAction}>
              <ArrowBackIcon />
              {backLabel}
            </Button>
          }
          nextButton={
            activeStep === STEPS.length - 1 ? (
              <Button
                size="small"
                variant="contained"
                onClick={handleSubmit}
                disabled={!canProceed || submitting}
                startIcon={
                  submitting ? (
                    <CircularProgress size={14} />
                  ) : (
                    <RocketLaunchIcon />
                  )
                }
              >
                {submitting ? "Deploying…" : "Deploy"}
              </Button>
            ) : (
              <Button
                size="small"
                onClick={() => setActiveStep((s) => s + 1)}
                disabled={!canProceed}
              >
                Next
                <ArrowForwardIcon />
              </Button>
            )
          }
        />
      ) : (
        <Stack direction="row" justifyContent="space-between">
          <Button startIcon={<ArrowBackIcon />} onClick={backAction}>
            {backLabel}
          </Button>
          {activeStep < STEPS.length - 1 ? (
            <Button
              variant="contained"
              endIcon={<ArrowForwardIcon />}
              onClick={() => setActiveStep((s) => s + 1)}
              disabled={!canProceed}
            >
              Next
            </Button>
          ) : (
            <Button
              variant="contained"
              color="primary"
              startIcon={
                submitting ? (
                  <CircularProgress size={16} color="inherit" />
                ) : (
                  <RocketLaunchIcon />
                )
              }
              onClick={handleSubmit}
              disabled={!canProceed || submitting}
            >
              {submitting ? "Deploying…" : "Save & Deploy"}
            </Button>
          )}
        </Stack>
      )}
    </Container>
  );
}
